import React, { useState, useEffect, useCallback, useRef } from 'react';
import { mix } from "@spa/utils/assetPathResolver";
import { useFilter } from "@spa/features/search_job/pc/contexts/FilterContext";
import {trackClickJobFilterSearch} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {useTrack} from "@spa/features/search_job/pc/contexts/TrackContext";
import {replace} from "react-router-dom";

/**
 * 単一選択のフィルターコンポーネント
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {Array<{value: string, label: string}>} props.options
 * @param {string} props.paramKey
 * @param {string} [props.width="w230"]
 * @param {string} [props.defaultValue]
 */
const SingleSelectFilter = ({
    label,
    options,
    paramKey,
    width = "w230",
    allJobsLabel,
    defaultValue,
    translations,
}) => {
    const { handleFilterChange, isFilterActive, getFilterValue } = useFilter();
    const [isOpen, setIsOpen] = useState(false);

    const initialFilterValue = useRef(getFilterValue(paramKey));
    const selectedValueForDisplay = initialFilterValue.current;

    const selected = options.find(opt => opt.value === selectedValueForDisplay);
    const displayLabel = selected?.label ?? label;
    const hasDefaultValue = defaultValue !== undefined;
    const isActive = hasDefaultValue || Boolean(selectedValueForDisplay);
    const showClearButton = !hasDefaultValue && Boolean(selectedValueForDisplay);

    const { channelName } = useTrack();

    useEffect(() => {
        if (hasDefaultValue && !getFilterValue(paramKey)) {
            handleFilterChange(paramKey, defaultValue);
        }
    }, [hasDefaultValue, paramKey, defaultValue, getFilterValue, handleFilterChange]);

    const handleOptionSelect = useCallback((option) => {
        trackClickJobFilterSearch(channelName);
        // allJobsLabel または value が "0" の場合はフィルター解除
        if (option.label === allJobsLabel || option.value === "0") {
            handleFilterChange(paramKey, null);
        } else {
            handleFilterChange(paramKey, option.value);
        }
        setIsOpen(false);
    }, [handleFilterChange, paramKey]);

    const handleClearClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        trackClickJobFilterSearch(channelName);
        handleFilterChange(paramKey, null);
    }, [handleFilterChange, paramKey]);

    // filter.jsのイベントハンドリング
    useEffect(() => {
        const clearBtnElement = document.querySelector(
            `.s-filter_btn_wrap.is-active[data-param-key="${paramKey}"] .s-filter_clearBtn`
        );
        if (!clearBtnElement) return;

        const handleJQueryClear = (e) => {
            if (!e.detail?.reactHandled) {
                handleClearClick(new MouseEvent('mouseup', { bubbles: true }));
            }
        };

        clearBtnElement.addEventListener('click', handleJQueryClear);
        return () => {
            clearBtnElement.removeEventListener('click', handleJQueryClear);
        };
    }, [handleClearClick, paramKey]);

    // モーダルの外側クリック時の処理
    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (e) => {
            if (!e.target.closest(`.s-filter_btn_wrap[data-param-key="${paramKey}"]`)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isOpen, paramKey]);

    const wrapperClassName = [
        "p-filter_btn_wrap s-filter_btn_wrap",
        hasDefaultValue && "p-filter_btn_wrap--select",
        isActive && "is-active",
        isOpen && "is-on"
    ].filter(Boolean).join(" ");

    // フィルターがアクティブな場合のみ allJobsLabel オプションを表示
    const filteredOptions = options.filter(option =>
        option.label !== allJobsLabel || isActive
    );

    return (
        <li>
            <div className={wrapperClassName} data-param-key={paramKey}>
                <button
                    type="button"
                    className="p-filter_btn s-filter_btn"
                    aria-expanded={isOpen}
                    aria-label={translations.item_filter.replace(':item', label)}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {displayLabel}
                </button>

                {showClearButton && (
                    <button
                        type="button"
                        className="p-filter_clearBtn s-filter_clearBtn"
                        onClick={handleClearClick}
                        aria-label={translations.remove_item_filter.replace(':item', selected?.label || label)}
                    >
                        <img
                            src={mix("webfront/images/icon/cross_bold.svg")}
                            className="p-filter_clearBtn_img"
                            alt={translations.remove_item_filter.replace(':item', label)}
                            width="9"
                            height="9"
                        />
                    </button>
                )}
            </div>

            <div className={`p-filter_select_wrap s-filter_menu_box${isOpen ? ' is-show' : ''}`}>
                <div
                    className={`p-filter_select_balloon p-filter_select_balloon--${width} s-filter_menu`}
                    tabIndex="0"
                    aria-label={translations.item_filter.replace(':item', label)}
                    role="listbox"
                >
                    <ul role="group">
                        {filteredOptions.map(option => (
                            <li key={option.value} className="p-filter_select_box">
                                <button
                                    type="button"
                                    className={`p-filter_select_item s-filter_select${selectedValueForDisplay === option.value ? ' is-selected' : ''}`}
                                    role="radio"
                                    aria-checked={selectedValueForDisplay === option.value}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    {option.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </li>
    );
};

export default SingleSelectFilter;
